import React, { useMemo } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { JsonEditor } from '@/components/ui/json-editor';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import type { BridgeTraceDetail, BridgeTraceObservation } from '@/services/langfuseBridge.service';

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? '');
  }
};

/** Depth of each observation from its parent chain (for indentation). */
function computeDepths(observations: BridgeTraceObservation[]): Map<string, number> {
  const byId = new Map(observations.map(o => [o.id, o]));
  const depths = new Map<string, number>();
  const depthOf = (o: BridgeTraceObservation, seen: Set<string>): number => {
    if (depths.has(o.id)) return depths.get(o.id)!;
    const parentId = o.parent_observation_id;
    let d = 0;
    if (parentId && byId.has(parentId) && !seen.has(parentId)) {
      seen.add(o.id);
      d = depthOf(byId.get(parentId)!, seen) + 1;
    }
    depths.set(o.id, d);
    return d;
  };
  observations.forEach(o => depthOf(o, new Set()));
  return depths;
}

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    {value === null || value === undefined ? (
      <p className="text-xs text-muted-foreground">No data.</p>
    ) : (
      <JsonEditor value={prettyJson(value)} readOnly minHeight="0px" maxHeight="14rem" />
    )}
  </div>
);

interface TraceInspectorProps {
  trace: BridgeTraceDetail | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Read-only inspector: the trace's input/output plus all of its spans. */
export const TraceInspector: React.FC<TraceInspectorProps> = ({ trace, isLoading, isError }) => {
  const depths = useMemo(() => computeDepths(trace?.observations ?? []), [trace?.observations]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-1">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError || !trace) {
    return <p className="p-2 text-sm text-destructive">Failed to load the trace.</p>;
  }

  const observations = trace.observations ?? [];

  return (
    <ScrollArea className="min-h-0 flex-1">
      <div className="space-y-4 pr-3">
        <Accordion type="multiple" defaultValue={['trace-output']} className="space-y-2">
          <AccordionItem value="trace-input" className="rounded-md border">
            <AccordionTrigger className="px-3 py-2 text-sm">Trace input</AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <JsonBlock label="input" value={trace.input} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="trace-output" className="rounded-md border">
            <AccordionTrigger className="px-3 py-2 text-sm">Trace output</AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <JsonBlock label="output" value={trace.output} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div>
          <div className="mb-1.5 px-1 text-xs font-medium uppercase text-muted-foreground">
            Spans ({observations.length})
          </div>
          {observations.length === 0 ? (
            <p className="px-1 text-sm text-muted-foreground">No spans available for this trace.</p>
          ) : (
            <Accordion type="multiple" className="space-y-1.5">
              {observations.map(obs => (
                <AccordionItem
                  key={obs.id}
                  value={obs.id}
                  className="rounded-md border"
                  style={{ marginLeft: (depths.get(obs.id) ?? 0) * 12 }}
                >
                  <AccordionTrigger className="px-3 py-2 text-sm">
                    <span className="flex items-center gap-2">
                      {obs.type && (
                        <Badge variant="secondary" className="px-1.5 py-0 text-[10px] uppercase">
                          {obs.type}
                        </Badge>
                      )}
                      <span className="font-medium">{obs.name || obs.id}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3 px-3 pb-3">
                    <JsonBlock label="input" value={obs.input} />
                    <JsonBlock label="output" value={obs.output} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </div>
    </ScrollArea>
  );
};
